import { Link } from "react-router-dom";
import { useState } from "react";
import {
  InstagramLogo, FacebookLogo, XLogo, YoutubeLogo, MapPin, WhatsappLogo, EnvelopeSimple, Clock, PaperPlaneTilt, Lightning,
} from "@phosphor-icons/react";
import { SITE } from "@/data/site";
import { useSettings } from "@/context/SettingsContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const COLS = [
  { title: "Shop", links: [
    { label: "Road Bikes", to: "/category/road" },
    { label: "Mountain Bikes", to: "/category/mountain" },
    { label: "E-Bikes", to: "/category/electric" },
    { label: "Gravel Bikes", to: "/category/gravel" },
    { label: "Specialized Framesets", to: "/category/framesets" },
    { label: "Sur-Ron Electric Bikes", to: "/category/surron" },
    { label: "Components & Parts", to: "/category/components" },
    { label: "All Bikes", to: "/shop" },
  ]},
  { title: "Services", links: [
    { label: "Bike Repairs", to: "/repairs" },
    { label: "Bike Servicing", to: "/servicing" },
    { label: "Bike Rentals", to: "/rentals" },
    { label: "Compare Bikes", to: "/compare" },
    { label: "Testimonials", to: "/testimonials" },
    { label: "FAQ", to: "/faq" },
  ]},
  { title: "Company", links: [
    { label: "About Us", to: "/about" },
    { label: "Blog", to: "/blog" },
    { label: "Partners", to: "/partners" },
    { label: "Resources", to: "/resources" },
    { label: "Contact", to: "/contact" },
    { label: "Shipping Policy", to: "/shipping" },
    { label: "Returns", to: "/returns" },
    { label: "Privacy Policy", to: "/privacy" },
  ]},
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const { settings } = useSettings();
  const c = settings.contact || {};
  const cAddress = c.address || SITE.address;
  const cPhone = c.phone || SITE.phone;
  const cEmail = c.email || SITE.email;
  const cHours = `Mon–Fri ${c.hours_weekday || "9–18"} · Sat ${c.hours_saturday || "10–17"}`;
  const social = {
    instagram: c.instagram || SITE.social.instagram,
    facebook: c.facebook || SITE.social.facebook,
    twitter: c.twitter || SITE.social.twitter,
    youtube: c.youtube || SITE.social.youtube,
  };

  const subscribe = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You're subscribed! Welcome to the ride.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <footer className="bg-[#0A0A0A] text-white" data-testid="site-footer">
      {/* Newsletter strip */}
      <div className="border-b border-white/10">
        <div className="container-px py-12">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6">
            <div>
              <p className="overline text-primary">Stay in the loop</p>
              <h3 className="mt-2 text-2xl sm:text-3xl font-black tracking-tight">Join 20,000+ riders for exclusive offers</h3>
            </div>
            <form onSubmit={subscribe} className="flex w-full max-w-md items-center gap-2 rounded-full bg-white/5 border border-white/10 p-1.5" data-testid="footer-newsletter-form">
              <EnvelopeSimple size={20} className="ml-3 text-white/50" />
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="flex-1 bg-transparent px-2 py-2 text-sm outline-none placeholder:text-white/40"
                data-testid="footer-newsletter-input"
              />
              <button disabled={busy} className="btn-primary !px-5 !py-2.5 text-sm shrink-0" data-testid="footer-newsletter-submit">
                {busy ? "…" : <>Subscribe <PaperPlaneTilt size={15} /></>}
              </button>
            </form>
          </div>
        </div>
      </div>

      <div className="container-px py-14">
        <div className="grid gap-10 lg:grid-cols-5">
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-2">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-primary font-heading font-black text-lg">E</span>
              <span className="font-heading text-xl font-black">Ebikes<span className="text-primary">&</span>Gear</span>
            </Link>
            <p className="mt-4 max-w-sm text-sm text-white/60 leading-relaxed">
              Your 2026 Specialized S-Works & Trek stockist. Premium carbon e-bikes, road, gravel and XC bikes plus professional maintenance parts — delivered across the UK & EU, from London to Lisbon.
            </p>
            <ul className="mt-6 space-y-3 text-sm text-white/70">
              <li className="flex items-start gap-3"><MapPin size={18} className="text-primary mt-0.5 shrink-0" /> {cAddress}</li>
              <li className="flex items-center gap-3"><WhatsappLogo size={18} className="text-primary shrink-0" /> <a href={`https://wa.me/${(settings.contact || {}).whatsapp || SITE.whatsapp}`} target="_blank" rel="noreferrer" className="hover:text-white" data-testid="footer-whatsapp-link">WhatsApp {cPhone}</a></li>
              <li className="flex items-center gap-3"><EnvelopeSimple size={18} className="text-primary shrink-0" /> <a href={`mailto:${cEmail}`} className="hover:text-white">{cEmail}</a></li>
              <li className="flex items-center gap-3"><Clock size={18} className="text-primary shrink-0" /> {cHours}</li>
            </ul>
            <div className="mt-6 flex gap-3">
              {[{ I: InstagramLogo, h: social.instagram }, { I: FacebookLogo, h: social.facebook }, { I: XLogo, h: social.twitter }, { I: YoutubeLogo, h: social.youtube }].map(({ I, h }, i) => (
                <a key={i} href={h} target="_blank" rel="noreferrer" className="grid h-10 w-10 place-items-center rounded-full bg-white/5 border border-white/10 hover:bg-primary hover:border-primary transition-colors">
                  <I size={18} />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title}>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/90">{col.title}</h4>
              <ul className="mt-4 space-y-2.5">
                {col.links.map((l) => (
                  <li key={l.to + l.label}>
                    <Link to={l.to} className="text-sm text-white/60 hover:text-primary transition-colors">{l.label}</Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-2xl border border-white/10 bg-white/[0.03] px-6 py-4 text-xs text-white/50">
          <span className="inline-flex items-center gap-2 text-white/70 font-semibold"><Lightning size={14} weight="fill" className="text-primary" /> Secure ordering</span>
          <span>Direct Bank Transfer</span><span>BACS</span><span>Faster Payments</span><span>SEPA (EU)</span>
          <span className="ml-auto">🔒 Order now — your bike is reserved 48 hours while you pay</span>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="container-px py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-white/50">
          <p>© {new Date().getFullYear()} Ebikes&Gear. All rights reserved.</p>
          <div className="flex gap-5">
            <Link to="/terms" className="hover:text-white">Terms</Link>
            <Link to="/privacy" className="hover:text-white">Privacy</Link>
            <Link to="/cookies" className="hover:text-white">Cookies</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
